import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  TimePicker,
  Switch,
  message,
  Card,
  Dropdown,
  Menu,
  InputNumber,
  Upload,
  Typography,
  Tooltip,
  ConfigProvider,
  Row,
  Col,
  Divider,
  Popconfirm
} from 'antd';
import {
  Copy,
  Download,
  Plus,
  Edit,
  Trash2,
  Search,
  RefreshCw,
  MoreVertical,
  Eye,
  FileText,
  Save,
  X,
  Filter,
  Settings
} from 'lucide-react';
import moment from 'moment-timezone';
import _ from 'underscore';
import http from '../interceptors/httpInterceptor';

const { Option } = Select;
const { Text } = Typography;
const { RangePicker } = DatePicker;
const { TextArea } = Input;

const JQGridReact = (props) => {
  // Destructure props with support for both camelCase and kebab-case from AngularJS
  const {
    domain,
    table,
    columnModel,
    columnOptions = {},
    filter,
    fvalue,
    search,
    config,
    type,
    parentdiv,
    open,
    filterData,
    requestQueryParams,
    gridOptions = {},
    dateFormat: dateFormatCamel = 'DD-MM-YYYY',
    datetimeFormat: datetimeFormatCamel = 'DD-MM-YYYY HH:mm:ss',
    // Support kebab-case attributes from AngularJS
    'date-format': dateFormatKebab = 'DD-MM-YYYY',
    'datetime-format': datetimeFormatKebab = 'DD-MM-YYYY HH:mm:ss'
  } = props;

  // Use the correct format values (handle both camelCase and kebab-case)
  const dateFormat = dateFormatCamel || dateFormatKebab || 'DD-MM-YYYY';
  const datetimeFormat = datetimeFormatCamel || datetimeFormatKebab || 'DD-MM-YYYY HH:mm:ss';

  console.log('Date format:', dateFormat);
  console.log('Datetime format:', datetimeFormat);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 25,
    total: 0
  });
  const [columns, setColumns] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [editingKey, setEditingKey] = useState('');
  const [searchText, setSearchText] = useState('');
  const [searchedColumn, setSearchedColumn] = useState('');
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [modalType, setModalType] = useState('add');
  const [currentRecord, setCurrentRecord] = useState(null);
  const [filters, setFilters] = useState({});
  const [sortField, setSortField] = useState('updated_on');
  const [sortOrder, setSortOrder] = useState('desc');
  const [columnConfig, setColumnConfig] = useState([]);
  const formRef = useRef();
  const tableRef = useRef();
  const [editingCell, setEditingCell] = useState(null);
  const [oldValue, setOldValue] = useState(null);

  // Configuration from props or defaults
  const gridHeight = gridOptions.gridHeight || '42vh';
  const searchEnabled = typeof gridOptions.searchEnabled === "undefined" ? true : gridOptions.searchEnabled;
  const embedded = gridOptions.embedded || false;
  const defaultPageSize = config?.pagesize || 25;
  const initialSortOrder = config?.sortorder || 'desc';
  const gridWidth = config?.gridwidth || 0;
  const initialSortName = config?.sortname || 'updated_on';
  const cellEditing = config?.editenabled !== false;
  const initialFilters = config?.filters || [];
  const isNavGridEnabled = config?.shownavGrid !== false;
  const navAdd = isNavGridEnabled;
  const navEdit = isNavGridEnabled;
  const navDelete = isNavGridEnabled;

  // API base URL
  const dataUrl = `/o/recs/${table}`;

  // Fetch column configuration and data
  useEffect(() => {
    fetchGridModel();
  }, [table, columnModel]);

  useEffect(() => {
    fetchData();
  }, [pagination.current, pagination.pageSize, sortField, sortOrder, filters]);

  const fetchGridModel = async () => {
    try {
      const project = columnModel?.join()?.toLowerCase() || '';
      const response = await http.get(`/o/${table}/listv2?project=${project}&filters=${JSON.stringify(initialFilters)}`);
      const gridData = response.data;
      
      setColumnConfig(gridData.columnData);
      const formattedColumns = formatColumns(gridData.columnData);
      setColumns(formattedColumns);
    } catch (error) {
      console.error('Error fetching grid model:', error);
      message.error('Failed to load grid configuration');
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.current,
        rows: pagination.pageSize,
        sidx: sortField,
        sord: sortOrder,
        ...filters
      };

      // Add filter data from props if available
      if (filterData) {
        Object.assign(params, filterData);
      }

      const response = await http.get(dataUrl, { params });
      const result = response.data;

      setData(result.rows || []);
      setPagination({
        ...pagination,
        total: result.total || 0
      });
    } catch (error) {
      console.error('Error fetching data:', error);
      message.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const formatColumns = (columnData) => {
    return columnData.map((col, index) => {
      const baseColumn = {
        title: col.displayName || col.name,
        dataIndex: col.name,
        key: col.name,
        width: col.width,
        editable: col.enableCellEdit !== false,
        sorter: true,
        ...getColumnTypeConfig(col)
      };

      // Make first column clickable to open record detail
      if (index === 0) {
        baseColumn.render = (text, record) => (
          <a 
            href={`/o/${table}/${record._id}`}
            onClick={(e) => {
              e.preventDefault();
              window.open(`/o/${table}/${record._id}`, '_blank');
            }}
            style={{ color: '#1890ff', cursor: 'pointer' }}
          >
            {text}
          </a>
        );
      }

      // Add cell editing for editable columns
      if (col.enableCellEdit !== false && cellEditing) {
        const originalRender = baseColumn.render;
        baseColumn.render = (text, record, rowIndex) => {
          const isEditing = editingCell?.rowIndex === rowIndex && editingCell?.columnName === col.name;
          
          if (isEditing) {
            return renderEditCell(col, text, record, rowIndex);
          }
          
          return (
            <div 
              onDoubleClick={() => handleCellEditStart(rowIndex, col.name, text, record)}
              style={{ minHeight: '40px', display: 'flex', alignItems: 'center' }}
            >
              {originalRender ? originalRender(text, record, rowIndex) : text}
            </div>
          );
        };
      }

      if (searchEnabled) {
        baseColumn.filterDropdown = ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
          <div style={{ padding: 8 }}>
            <Input
              placeholder={`Search ${col.name}`}
              value={selectedKeys[0]}
              onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
              onPressEnter={() => handleSearch(selectedKeys, confirm, col.name)}
              style={{ marginBottom: 8, display: 'block' }}
            />
            <Space>
              <Button
                type="primary"
                onClick={() => handleSearch(selectedKeys, confirm, col.name)}
                icon={<Search size={14} />}
                size="small"
                style={{ width: 90 }}
              >
                Search
              </Button>
              <Button onClick={() => handleReset(clearFilters)} size="small" style={{ width: 90 }}>
                Reset
              </Button>
            </Space>
          </div>
        );
        baseColumn.filterIcon = filtered => <Search style={{ color: filtered ? '#1890ff' : undefined }} size={14} />;
      }

      return baseColumn;
    });
  };

  const renderEditCell = (col, value, record, rowIndex) => {
    const handleSave = (newValue) => {
      handleCellSave(record._id, col.name, newValue, rowIndex);
    };

    const handleCancel = () => {
      setEditingCell(null);
      setOldValue(null);
    };

    switch (col.input_type) {
      case 'number':
        return (
          <InputNumber
            defaultValue={value}
            autoFocus
            onPressEnter={(e) => handleSave(e.target.value)}
            onBlur={(e) => handleSave(e.target.value)}
            style={{ width: '100%' }}
          />
        );
      
      case 'date':
        return (
          <DatePicker
            defaultValue={value ? moment(value) : null}
            format={dateFormat}
            autoFocus
            onChange={(date) => handleSave(date ? date.format('YYYY-MM-DD') : '')}
            onBlur={() => handleCancel()}
            style={{ width: '100%' }}
          />
        );
      
      case 'datetime':
        return (
          <DatePicker
            showTime
            defaultValue={value ? moment(value) : null}
            format={datetimeFormat}
            autoFocus
            onChange={(date) => handleSave(date ? date.toISOString() : '')}
            onBlur={() => handleCancel()}
            style={{ width: '100%' }}
          />
        );
      
      case 'option':
        const optionValues = col.editoptions?.value || {};
        return (
          <Select
            defaultValue={value}
            autoFocus
            onChange={handleSave}
            onBlur={() => handleCancel()}
            style={{ width: '100%' }}
          >
            {Object.entries(optionValues).map(([key, val]) => (
              <Option key={key} value={key}>{val}</Option>
            ))}
          </Select>
        );
      
      case 'checkbox':
        return (
          <Switch
            defaultChecked={value}
            autoFocus
            onChange={handleSave}
            onBlur={() => handleCancel()}
          />
        );
      
      case 'text':
      default:
        return (
          <Input
            defaultValue={value}
            autoFocus
            onPressEnter={(e) => handleSave(e.target.value)}
            onBlur={(e) => handleSave(e.target.value)}
            style={{ width: '100%' }}
          />
        );
    }
  };

  const handleCellEditStart = (rowIndex, columnName, value, record) => {
    setEditingCell({ rowIndex, columnName });
    setOldValue(value);
  };

  const handleCellSave = async (recordId, columnName, newValue, rowIndex) => {
    try {
      // Convert values for backend
      let processedValue = newValue;
      
      // Handle checkbox values
      if (typeof newValue === 'boolean') {
        processedValue = newValue;
      }
      
      // Prepare the update data
      const updateData = {
        _id: recordId,
        [columnName]: processedValue,
        _oldValue: oldValue
      };

      // Send update request
      const response = await http.put(`/o/${table}/${recordId}`, updateData);
      
      if (response.status === 200) {
        message.success('Cell updated successfully');
        
        // Update local data
        const newData = [...data];
        const recordIndex = newData.findIndex(item => item._id === recordId);
        
        if (recordIndex > -1) {
          newData[recordIndex] = {
            ...newData[recordIndex],
            [columnName]: processedValue
          };
          setData(newData);
        }
      } else {
        message.error('Failed to update cell');
      }
    } catch (error) {
      console.error('Error updating cell:', error);
      message.error('Failed to update cell');
      
      // Revert local changes on error
      const newData = [...data];
      const recordIndex = newData.findIndex(item => item._id === recordId);
      
      if (recordIndex > -1) {
        newData[recordIndex] = {
          ...newData[recordIndex],
          [columnName]: oldValue
        };
        setData(newData);
      }
    } finally {
      setEditingCell(null);
      setOldValue(null);
    }
  };

  const getColumnTypeConfig = (col) => {
    switch (col.input_type) {
      case 'number':
        return {
          render: (text) => text,
          sorter: (a, b) => a[col.name] - b[col.name],
          filterDropdown: ({ setSelectedKeys, selectedKeys, confirm }) => (
            <div style={{ padding: 8 }}>
              <InputNumber
                style={{ width: 100, marginBottom: 8 }}
                value={selectedKeys[0]}
                onChange={value => setSelectedKeys(value ? [value] : [])}
                onPressEnter={() => handleSearch(selectedKeys, confirm, col.name)}
              />
              <Space>
                <Button
                  type="primary"
                  onClick={() => handleSearch(selectedKeys, confirm, col.name)}
                  size="small"
                  style={{ width: 90 }}
                >
                  Filter
                </Button>
              </Space>
            </div>
          )
        };
      
      case 'date':
        return {
          render: (text) => text ? moment(text).format(dateFormat) : '',
          sorter: (a, b) => moment(a[col.name]).unix() - moment(b[col.name]).unix(),
          filterDropdown: ({ setSelectedKeys, selectedKeys, confirm }) => (
            <div style={{ padding: 8 }}>
              <DatePicker
                style={{ marginBottom: 8 }}
                format={dateFormat}
                onChange={date => setSelectedKeys(date ? [date.format('YYYY-MM-DD')] : [])}
                onPressEnter={() => handleSearch(selectedKeys, confirm, col.name)}
              />
              <Space>
                <Button
                  type="primary"
                  onClick={() => handleSearch(selectedKeys, confirm, col.name)}
                  size="small"
                  style={{ width: 90 }}
                >
                  Filter
                </Button>
              </Space>
            </div>
          )
        };
      
      case 'datetime':
        return {
          render: (text) => text ? moment(text).format(datetimeFormat) : '',
          sorter: (a, b) => moment(a[col.name]).unix() - moment(b[col.name]).unix()
        };
      
      case 'option':
        const optionValues = col.editoptions?.value || {};
        return {
          render: (text) => optionValues[text] || text,
          filters: Object.entries(optionValues).map(([key, value]) => ({
            text: value,
            value: key
          }))
        };
      
      case 'checkbox':
        return {
          render: (text) => text ? 'Yes' : 'No',
          filters: [
            { text: 'Yes', value: true },
            { text: 'No', value: false }
          ]
        };
      
      case 'text':
      default:
        return {
          render: (text) => text
        };
    }
  };

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
    setFilters(prev => ({
      ...prev,
      [dataIndex]: selectedKeys[0]
    }));
  };

  const handleReset = (clearFilters) => {
    clearFilters();
    setSearchText('');
    setFilters(prev => {
      const newFilters = { ...prev };
      delete newFilters[searchedColumn];
      return newFilters;
    });
  };

  const handleTableChange = (pagination, filters, sorter) => {
    setPagination(pagination);
    setFilters(filters);
    if (sorter.field) {
      setSortField(sorter.field);
      setSortOrder(sorter.order === 'ascend' ? 'asc' : 'desc');
    }
  };

  const handleAdd = () => {
    setModalType('add');
    setCurrentRecord(null);
    setIsModalVisible(true);
  };

  const handleEdit = (record) => {
    setModalType('edit');
    setCurrentRecord(record);
    setIsModalVisible(true);
  };

  const handleDelete = async (record) => {
    try {
      await http.delete(`/o/${table}/${record._id}`);
      message.success('Record deleted successfully');
      fetchData();
    } catch (error) {
      console.error('Error deleting record:', error);
      message.error('Failed to delete record');
    }
  };

  const handleModalOk = async () => {
    try {
      const values = await formRef.current.validateFields();
      
      if (modalType === 'add') {
        await http.post(`/o/${table}`, values);
        message.success('Record added successfully');
      } else {
        await http.put(`/o/${table}/${currentRecord._id}`, values);
        message.success('Record updated successfully');
      }
      
      setIsModalVisible(false);
      fetchData();
    } catch (error) {
      console.error('Error saving record:', error);
      message.error('Failed to save record');
    }
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
  };

  const handleRefresh = () => {
    fetchData();
  };

  const handleExport = () => {
    // Implement export functionality
    message.info('Export functionality to be implemented');
  };

  const rowSelection = {
    onChange: (selectedRowKeys, selectedRows) => {
      setSelectedRows(selectedRows);
    },
  };

  const renderModalForm = () => {
    return (
      <Modal
        title={modalType === 'add' ? 'Add New Record' : 'Edit Record'}
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        width={600}
      >
        <Form
          ref={formRef}
          layout="vertical"
          initialValues={currentRecord || {}}
        >
          {columnConfig.map(col => (
            <Form.Item
              key={col.name}
              name={col.name}
              label={col.label || col.name}
              rules={[{ required: col.mandatory, message: `${col.label} is required` }]}
            >
              {renderFormField(col)}
            </Form.Item>
          ))}
        </Form>
      </Modal>
    );
  };

  const renderFormField = (col) => {
    switch (col.input_type) {
      case 'number':
        return <InputNumber style={{ width: '100%' }} />;
      
      case 'date':
        return <DatePicker style={{ width: '100%' }} format={dateFormat} />;
      
      case 'datetime':
        return <DatePicker showTime style={{ width: '100%' }} format={datetimeFormat} />;
      
      case 'option':
        const options = col.editoptions?.value || {};
        return (
          <Select>
            {Object.entries(options).map(([key, value]) => (
              <Option key={key} value={key}>{value}</Option>
            ))}
          </Select>
        );
      
      case 'checkbox':
        return <Switch />;
      
      case 'text':
      default:
        return <Input />;
    }
  };

  const actionMenu = (record) => (
    <Menu>
      <Menu.Item key="edit" icon={<Edit size={14} />} onClick={() => handleEdit(record)}>
        Edit
      </Menu.Item>
      <Menu.Item key="view" icon={<Eye size={14} />}>
        View
      </Menu.Item>
      <Menu.Divider />
      <Menu.Item key="delete" icon={<Trash2 size={14} />} danger>
        <Popconfirm
          title="Are you sure to delete this record?"
          onConfirm={() => handleDelete(record)}
          okText="Yes"
          cancelText="No"
        >
          Delete
        </Popconfirm>
      </Menu.Item>
    </Menu>
  );

  // Add action column if needed
  const actionColumn = {
    title: 'Actions',
    key: 'actions',
    width: 100,
    render: (_, record) => (
      <Dropdown overlay={actionMenu(record)} trigger={['click']}>
        <Button type="text" icon={<MoreVertical size={14} />} />
      </Dropdown>
    ),
  };

  const finalColumns = [...columns, actionColumn];

  return (
    <Card 
      title={table.replace(/[_-]/g, " ").toUpperCase()}
      extra={
        <Space>
          {navAdd && (
            <Button type="primary" icon={<Plus size={14} />} onClick={handleAdd}>
              Add
            </Button>
          )}
          <Button icon={<RefreshCw size={14} />} onClick={handleRefresh}>
            Refresh
          </Button>
          <Button icon={<Download size={14} />} onClick={handleExport}>
            Export
          </Button>
        </Space>
      }
    >
      <Table
        ref={tableRef}
        columns={finalColumns}
        dataSource={data}
        rowKey="_id"
        pagination={pagination}
        loading={loading}
        onChange={handleTableChange}
        rowSelection={rowSelection}
        scroll={{ y: gridHeight }}
        size="middle"
        components={{
          body: {
            row: (props) => <tr {...props} style={{ height: '40px' }} />
          }
        }}
      />
      {renderModalForm()}
    </Card>
  );
};

export default JQGridReact;
